import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Popover from "@mui/material/Popover";
import MUIButton from "@mui/material/Button";
import { SxProps } from "@mui/system";
import AuthService from "../../common/AuthService";
import { getSettings } from "../../webapi/service/getSettings";
import { setSettings } from "../../webapi/service/setSettings";
import "../main.css";

function stringAvatar(name: string, sx?: SxProps) {
  const split = name.split(" ");
  let children = undefined;
  if (split.length > 2) {
    children = split[0][0] + split[1][0];
  } else if (split.length === 1) {
    children = split[0][0];
  }
  return { sx: sx, children: children };
}

export interface IProps extends WithTranslation {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * ログインユーザー名
   */
  name?: string;
  /**
   * ログアウトが押された場合に呼び出されます
   */
  onLogout?: () => void;
  /**
   * ライセンスボタンが押された場合に呼び出されます
   */
  onLicense?: () => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
}

interface IState {
  /**
   * ポップアップElement
   */
  anchorEl: null | HTMLElement;
  /**
   * 自動削除
   * ON : true, OFF : false
   */
  autoDelete: boolean;
}

class AccountIcon extends React.Component<IProps, IState> {
  private readonly AvatarLargeIconSize: SxProps = { width: 64, height: 64 };

  constructor(props: IProps) {
    super(props);

    this.state = { anchorEl: null, autoDelete: false };

    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleShowLicense = this.handleShowLicense.bind(this);
    this.handleChangeAutoDelete = this.handleChangeAutoDelete.bind(this);
  }

  handleOpen(event: React.MouseEvent<HTMLElement>) {
    const { authService, onCommunicationError } = this.props;
    const { currentTarget } = event;
    getSettings({
      authService: authService,
      success: (dataautodelete) => {
        this.setState({ anchorEl: currentTarget, autoDelete: dataautodelete });
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleClose() {
    this.setState({ anchorEl: null });
  }

  handleShowLicense() {
    const { onLicense } = this.props;
    this.handleClose();
    if (onLicense) {
      onLicense();
    }
  }

  handleChangeAutoDelete(e: React.ChangeEvent<HTMLSelectElement>) {
    const { authService, onCommunicationError } = this.props;
    const { value } = e.target;
    const dataautodelete = value === "ON" ? true : false;
    setSettings({
      authService: authService,
      dataautodelete: dataautodelete,
      success: () => {
        this.setState({ autoDelete: dataautodelete });
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  getAvatar(name: string | undefined, sx?: SxProps) {
    if (name) {
      return <Avatar {...stringAvatar(name, sx)} />;
    } else {
      return <Avatar sx={sx} />;
    }
  }

  render() {
    const { onLogout, name, t } = this.props;
    const { anchorEl, autoDelete } = this.state;
    const open = Boolean(anchorEl);
    return (
      <div>
        <IconButton
          size="large"
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          color="inherit"
          onClick={this.handleOpen}
        >
          <div className="account-icon">
            {this.getAvatar(name)}
            <div className="name">{name}</div>
          </div>
        </IconButton>
        <Popover
          anchorEl={anchorEl}
          open={open}
          onClose={this.handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <div className="account-menu">
            <div className="logout">
              <MUIButton variant="text" onClick={onLogout}>
                {t("account_logout")}
              </MUIButton>
            </div>
            <div className="icon">
              {this.getAvatar(name, this.AvatarLargeIconSize)}
            </div>
            <div className="dataautodelete">
              <div>{t("settings_dataautodelete")}</div>
              <div>
                <select
                  value={autoDelete ? "ON" : "OFF"}
                  onChange={this.handleChangeAutoDelete}
                >
                  <option value="ON">{t("settings_dataautodelete_on")}</option>
                  <option value="OFF">
                    {t("settings_dataautodelete_off")}
                  </option>
                </select>
              </div>
            </div>
            <div className="navi">
              <Button
                variant="primary"
                className="button"
                onClick={this.handleShowLicense}
              >
                {t("account_license")}
              </Button>
            </div>
          </div>
        </Popover>
      </div>
    );
  }
}

export default withTranslation()(AccountIcon);
